import React from "react";
import PropTypes from "prop-types";
import { Tab, Label, Button } from "rbx";
import { format } from "date-fns/esm";
import {
  Box
} from "@mui/material";
import {
  GenericTable,
  AccordionItem,
  BBCardSelectMUI,
  AccordionDetailsItem
} from "../../../../components";
import MobileTable from "../../../../components/ReportsTable/MobileTable";
import BoxLoader from "../../../../components/DetailedDataTable/BoxLoader/BoxLoader";


const isWpqExpirationWithinOneMonth = (expirationDate) => {
  if (!expirationDate) return false;
  const exp = new Date(expirationDate);
  if (Number.isNaN(exp.getTime())) return false;
  const now = new Date();
  const oneMonthFromNow = new Date(now);
  oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);
  return exp <= oneMonthFromNow;
};

const Table = ({
  employeeData,
  editMode,
  permissionLevels,
  TAB_STATES,
  setTabState,
  COLUMNS,
  handleRowClick,
  tabState = "skills",
  mobile,
  loading,
  onWpqDownload,
  onSendWpqRenewEmail,
}) => mobile ? (
  <div className="bball-mobile-table" style={{ width: "80%" }}>
    <br />
    <MobileTable
      bbCard
      showLoadingBar
      headerActions={
        <React.Fragment>
          <Box mt={1} width="90%">
            <BBCardSelectMUI value={tabState} onChange={setTabState} />
          </Box>
        </React.Fragment>
      }
      loading={loading}
      title="Qualifications & Licenses"
    >
      {loading ? (
        <BoxLoader />
      ) : (
        <React.Fragment>
          {tabState === "skills" && (
            employeeData[tabState]?.map((item) => (
              <AccordionItem
                key={item?.licenseNumber}
                title={item?.codes?.title}
                width="90%"
              >
                <AccordionDetailsItem
                  label="Points"
                  value={item?.codes?.points}
                />
                <AccordionDetailsItem
                  label="Issue Date"
                  value={format(new Date(item?.issueDate), "MM-dd-yyyy") || "--"}
                />
              </AccordionItem>
            ))
          )}
          {tabState === "licenses" && (
            employeeData[tabState]?.map((item) => (
              <AccordionItem
                info={format(new Date(item?.expDate), "MM-dd-yyyy")}
                key={item?.licenseNumber}
                title={item?.codes?.title}
                width="90%"
              >
                <AccordionDetailsItem
                  label="Level"
                  value={item?.codes?.level}
                />
                <AccordionDetailsItem
                  label="State"
                  value={item?.codes?.state}
                />
                <AccordionDetailsItem
                  label="Points"
                  value={item?.codes?.points}
                />
                <AccordionDetailsItem
                  label="License #"
                  value={item?.licenseNumber}
                />
                <AccordionDetailsItem
                  label="Issue Date"
                  value={format(new Date(item?.issueDate), "MM-dd-yyyy") || "--"}
                />
              </AccordionItem>
            ))
          )}
          {tabState === "certifications" && (
            employeeData[tabState]?.map((item) => (
              <AccordionItem
                info={format(new Date(item?.expDate), "MM-dd-yyyy")}
                key={item?.licenseNumber}
                title={item?.codes?.title}
                width="90%"
              >
                <AccordionDetailsItem
                  label="Points"
                  value={item?.codes?.points}
                />
                <AccordionDetailsItem
                  label="Certifiacation #"
                  value={item?.certificationNumber}
                />
                <AccordionDetailsItem
                  label="Issue Date"
                  value={format(new Date(item?.issueDate), "MM-dd-yyyy") || "--"}
                />
              </AccordionItem>
            ))
          )}
          {tabState === "wpq" && (
            employeeData[tabState]?.map((item) => (
              <AccordionItem
                key={item?.id || item?.name}
                title={item?.name || "WPQ"}
                width="90%"
              >
                <AccordionDetailsItem
                  label="WPQ Procedure"
                  value={item?.wpqProcedure || "--"}
                />
                <AccordionDetailsItem
                  label="Expiration Date"
                  value={(() => {
                    if (!item?.expirationDate) return "--";
                    const d = new Date(item.expirationDate);
                    if (Number.isNaN(d.getTime())) return "--";
                    const isExpired = d < new Date();
                    return (
                      <span style={{ color: isExpired ? "red" : undefined }}>
                        {format(d, "MM-dd-yyyy")}
                      </span>
                    );
                  })()}
                />
                <AccordionDetailsItem
                  label="Points"
                  value={
                    item?.points ?? item?.points === 0 ? item.points : "--"
                  }
                />
                <div style={{ marginTop: "0.5rem" }}>
                  <Button
                    color="info"
                    size="small"
                    style={{ backgroundColor: "#052e6f", borderColor: "#052e6f" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onWpqDownload?.(item?.externalId);
                    }}
                  >
                    Download
                  </Button>
                  {item?.id && (
                    <Button
                      color="info"
                      size="small"
                      disabled={!isWpqExpirationWithinOneMonth(item?.expirationDate)}
                      style={{
                        backgroundColor: isWpqExpirationWithinOneMonth(item?.expirationDate) ? "#052e6f" : "#9ca3af",
                        borderColor: isWpqExpirationWithinOneMonth(item?.expirationDate) ? "#052e6f" : "#6b7280",
                        color: !isWpqExpirationWithinOneMonth(item?.expirationDate) ? "#374151" : undefined,
                        marginLeft: "0.5rem",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isWpqExpirationWithinOneMonth(item?.expirationDate)) {
                          onSendWpqRenewEmail?.(item.id);
                        }
                      }}
                    >
                      Send Renew Email
                    </Button>
                  )}
                </div>
              </AccordionItem>
            ))
          )}
        </React.Fragment>
      )}
    </MobileTable>
  </div>
) : (
    <div className="bball-card-table-container">
      <div className="center-section">
        <div className="center-table">
          <div className="center-table-wrapper">
            <h3 style={{ marginTop: "10px" }}>Qualifications & Licenses</h3>

            <Tab.Group
              align="centered"
              style={{ marginBottom: 0, marginTop: "15px" }}
            >
              {/* supervisor can only edit skills */}
              {editMode && permissionLevels.fieldSupervisor ? (
                <Tab
                  active
                  key={TAB_STATES.skills}
                  onClick={() => setTabState(TAB_STATES.skills)}
                >
                  <Label className="modal-label">SKILLS</Label>
                </Tab>
              ) : (
                Object.keys(TAB_STATES).map((key) => {
                  const value = TAB_STATES[key];
                  const displayLabel =
                    value === "wpq"
                      ? "WPQ"
                      : value.charAt(0).toUpperCase() + value.slice(1);
                  return (
                    <Tab
                      active={tabState === value}
                      key={key}
                      onClick={() => setTabState(value)}
                    >
                      <Label className="modal-label">{displayLabel}</Label>
                    </Tab>
                  );
                })
              )}
            </Tab.Group>


            <GenericTable
              columns={COLUMNS[tabState]}
              data={employeeData[tabState] || []}
              isPaginated={false}
              loading={loading}
              onRowClick={(row) => (editMode ? handleRowClick(row) : null)}
            />
          </div>
        </div>
      </div>
    </div>
  )


Table.propTypes = {
  employeeData: PropTypes.object,
  editMode: PropTypes.bool,
  permissionLevels: PropTypes.object,
  TAB_STATES: PropTypes.object,
  setTabState: PropTypes.func,
  COLUMNS: PropTypes.object,
  handleRowClick: PropTypes.func,
  tabState: PropTypes.string,
  mobile: PropTypes.bool,
  loading: PropTypes.bool,
  onWpqDownload: PropTypes.func,
  onSendWpqRenewEmail: PropTypes.func,
};

Table.defaultProps = {
  employeeData: {},
  editMode: false,
  permissionLevels: {},
  TAB_STATES: {},
  setTabState: () => null,
  COLUMNS: {},
  handleRowClick: () => null,
  tabState: "",
  mobile: false,
  loading: false,
  onWpqDownload: () => null,
  onSendWpqRenewEmail: () => null,
};

export default Table;
